import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { matchBlocks } from './highlightModel';

// New UI: when a header field has focus, outline on the document where the scanner read that value.
// Text lines come from GET /api/invoicedocument/textblocks (read-only, the scanner's stored block JSON). The outlines
// are drawn in a layer over the viewer's canvas; the viewer itself is unchanged.
const pageShown = viewer => {
	const label = [...viewer.querySelectorAll('div, span')].find(e => e.children.length === 0 && /^\d+ \/ \d+$/.test(e.textContent.trim()));
	return label ? Number(label.textContent.split('/')[0]) : 1;
};
const fieldValue = el => (el.tagName === 'SELECT' ? (el.selectedOptions[0] ? el.selectedOptions[0].text : '') : el.value);

export default function FieldHighlight({ rootRef, modelDefId, fileLocation, companyCode }) {
	const [blocks, setBlocks] = useState([]);
	const [boxes, setBoxes] = useState([]);
	const [host, setHost] = useState(null);
	const valueRef = useRef('');

	useEffect(() => {
		setBlocks([]);
		if (!fileLocation) return undefined;
		let live = true;
		const q = new URLSearchParams({ invoiceDocumentFileLocation: fileLocation });
		if (companyCode != null) q.set('companyCode', companyCode);
		httpGet(`/api/invoicedocument/textblocks/${modelDefId}?${q}`)
			.then(r => { if (live && Array.isArray(r.data)) setBlocks(r.data); }, () => { });
		return () => { live = false; };
	}, [modelDefId, fileLocation, companyCode]);

	useEffect(() => {
		const root = rootRef.current;
		if (!root || !blocks.length) return undefined;
		const draw = () => {
			const viewer = root.querySelector('#pdfContainer');
			const canvas = viewer && (viewer.querySelector('canvas[id^="the-canvas-"]') || viewer.querySelector('#imgInvoiceDocument'));
			if (!viewer || !canvas || !canvas.offsetWidth || !valueRef.current) { setBoxes([]); return; }
			const page = pageShown(root.querySelector('.col-left') || viewer);
			setHost(viewer);
			setBoxes(matchBlocks(blocks, valueRef.current, page).map(b => ({
				left: canvas.offsetLeft + b.left * canvas.offsetWidth, top: canvas.offsetTop + b.top * canvas.offsetHeight,
				width: b.width * canvas.offsetWidth, height: b.height * canvas.offsetHeight
			})));
		};
		const onFocus = e => {
			const el = e.target;
			if (!el.closest || !el.closest('.apw2-ed-form') || !/^(INPUT|SELECT)$/.test(el.tagName)) return;
			valueRef.current = fieldValue(el);
			draw();
		};
		const onBlur = () => { valueRef.current = ''; setBoxes([]); };
		root.addEventListener('focusin', onFocus);
		root.addEventListener('focusout', onBlur);
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(draw) : null;
		const viewer = root.querySelector('#pdfContainer');
		if (ro && viewer) ro.observe(viewer);
		return () => { root.removeEventListener('focusin', onFocus); root.removeEventListener('focusout', onBlur); if (ro) ro.disconnect(); };
	}, [rootRef, blocks]);

	if (!host || !boxes.length) return null;
	return createPortal(
		<div className="apw2-hl-layer" aria-hidden="true">
			{boxes.map((b, i) => <span key={i} className="apw2-hl" style={{ left: b.left - 3, top: b.top - 2, width: b.width + 6, height: b.height + 4 }} />)}
		</div>,
		host
	);
}
